import React, {
    useState, useEffect, useCallback, useContext, useMemo
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { withRouter } from 'react-router-dom';
import { useDataRefresh, withViewModelService } from 'gw-portals-viewmodel-react';
import { renderContentFromMetadataInternal } from '@jutro/uiconfig';
import { useAuthentication } from 'gw-digital-auth-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import {
    AcceptWorkActionPopup,
    CommonWorkActionPopup,
    AddQuoteModal,
    DelayWorkActionPopup,
    AddInvoicePopup,
    WithdrawInvoicePopup,
    ReplyMessagePopup,
    CreateNewMessagePopup,
    UpdateMedicalPackagePopup,
    UpdateTheftResolutionPopup,
    AddDocumentPopupAX,
    UpdateAmountServicePopupAX,
} from 'gw-capability-servicerequest-react';
import {
    Loader, useModal, FormattedDate, Button, ToastProvider, Link
} from '@jutro/components';
import {  Currency as CurrencyField } from 'gw-components-platform-react';
import { messages as commonMessages } from 'gw-platform-translations';
import metadata from './ServiceRequestDetailsPage.metadata.json5';
import styles from './ServiceRequestDetailsPage.module.scss';
import messages from './ServiceRequestDetailsPage.messages';
import VehicleDetailsComponentAX from '../../components/VehicleDetailsComponent_AX/VehicleDetailsComponent_AX';

function evaluateCanAddInvoiceAX(details) {
    if (!details.ax_IsAuthForRequestPayment || details.status === 'declined'
     || details.status === 'canceled') {
        return false;
    }
    if (details.relatedBulkInvoice) {
        return !details.relatedBulkInvoice;
    }
    if (details.invoices.length === 0) {
        // has no invoices... let them to add a new one..
        return true;
    }
    // all existing invoices have been withdrawn, rejected, stoped and voided.
    const allWithdrawnInvoices = _.filter(details.invoices, (item) => item.status === 'withdrawn' ||
        item.status === 'rejected' || item.status === 'ax_stoped' || item.status === 'ax_voided');
    return details.invoices.length === allWithdrawnInvoices.length;
}

function ServiceRequestDetailsPage(props) {
    const { match, viewModelService } = props;
    const { params } = match;
    const { serviceRequestNumber } = params;
    const { authHeader } = useAuthentication();
    const [isLoading, setLoadingState] = useState(false);
    const [serviceItems, setServiceItems] = useState({});
    const [serviceDetails, setServiceDetails] = useState({ otherOperations: [] });
    const [adjusterService, setAdjusterService] = useState([]);
    const [canAddInvoiceAX, updateCanAddInvoiceAX] = useState(false);
    const translator = useContext(TranslatorContext);
    const { refreshData } = useDataRefresh();
    const { showModal,showAlert,showConfirm } = useModal();

    const PRIMARY_PHONE_TYPE_MAP = {
        home: 'homeNumber',
        mobile: 'cellNumber',
        work: 'workNumber'
    };

    const readValue = useCallback((id, path) => {
        if (path === 'otherOperations') {
            return _.get(serviceDetails, path);
        }
        return _.get(serviceItems, path);
    }, [serviceDetails, serviceItems]);

    const handleGenericErrorModal = useCallback((error) => {
        showAlert({
            title: messages.genericErrorTitleMsg,
            message: translator(messages.genericErrorBodyMsg, {
                codeNumber: error.errorCode, codeKey: error.appErrorCode
            }),
            status: 'error',
            icon: 'mi-error-outline'
        });
    }, [translator]);

    const lookupActionNameMessageKey = useCallback(
        (action) => {
            const { isReviseQuote } = serviceItems;
            const { quoteExists } = serviceDetails;
            if (action) {
                if (action === 'addquote') {
                    if (isReviseQuote || quoteExists) {
                        return translator(messages.actionReviseQuote);
                    }
                    return translator(messages.actionAddQuote);
                }
                if (action === 'vendordelayed') { // not an operation in CC, but an available action
                    return translator(messages.actionReportADelay);
                }
                if (action === 'cancelServiceFinished') { // not an operation in CC, but an available action
                    return translator(messages.actionCancelServiceFinished);
                }
                return translator(messages[action]);
            }
            return null;
        }, [serviceDetails, serviceItems, translator]
    );

    const translateMedicalPackageForCode = useCallback((code) => {
        return code ? translator({
            id: `typekey.AX_MedicalPackage.${code}`,
            defaultMessage: 'Not found'
        }) : null;
    }, [translator]);

    const translateTheftResolutionForCode = useCallback((code) => {
        return code ? translator({
            id: `typekey.AX_TheftResolution.${code}`,
            defaultMessage: 'Not found'
        }) : null;
    }, [translator]);

    const getServiceRequestDetails = useCallback(
        () => {
            setLoadingState(true);
            let authCanCancelServiceFinished = false;
            ServiceRequestService.hasAuthorityAX('AX_CANCEL_SERVICE_FINISHED', authHeader)
                .then((response) => {
                    authCanCancelServiceFinished = response;
                }).catch((error) => {
                    showAlert({
                        title: translator(messages.genericErrorTitleMsg),
                        message: error.baseError,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                });
            ServiceRequestService.getServiceRequestDetails(serviceRequestNumber, authHeader)
                .then((response) => {
                    const serviceData = {};
                    setServiceItems(response);
                    const shouldDisplayCanAddInvoiceAX = evaluateCanAddInvoiceAX(response);
                    const shouldDisplayCancelServiceFinished = response.status === 'workcomplete'
                     && authCanCancelServiceFinished
                     && !response.ax_CanCancelServiceFinished;

                    serviceData.isOverdue = response.isOverdue;
                    serviceData.quoteExists = !_.isUndefined(response.latestQuote);
                    const hasDefaultOperation = !_.isUndefined(response.defaultOperation);
                    serviceData.firstOperation = hasDefaultOperation
                        ? response.defaultOperation
                        : _.head(response.otherOperations);
                    serviceData.otherOperations = hasDefaultOperation
                        ? response.otherOperations
                        : _.tail(response.otherOperations);
                    if (shouldDisplayCancelServiceFinished) {
                        serviceData.otherOperations.push('cancelServiceFinished');
                    }
                    serviceData.dueDate = response.serviceDateApplies
                        ? response.expectedServiceCompletionDate
                        : response.expectedQuoteCompletionDate;

                    if (!shouldDisplayCanAddInvoiceAX) {
                        if (serviceData.firstOperation === 'addinvoice') {
                            serviceData.firstOperation = undefined;
                        }
                        if (serviceData.otherOperations.includes('addinvoice')) {
                            serviceData.otherOperations = serviceData.otherOperations.filter((opt) => opt !== 'addinvoice');
                        }
                    }

                    const customerContactPreferredNumberType = _.get(PRIMARY_PHONE_TYPE_MAP,
                        response.customerContact.primaryPhoneType);
                    const claimHandlerPreferredNumberType = _.get(PRIMARY_PHONE_TYPE_MAP,
                        response.claimHandler.primaryPhoneType);

                    if (response.adjusterService !== undefined) {
                        const adjusterPreferredNumberType = _.get(PRIMARY_PHONE_TYPE_MAP,
                            response.adjusterService.claimHandler.primaryPhoneType);
                        // eslint-disable-next-line max-len
                        serviceData.adjusterPreferredNumber = _.get(response.adjusterService.claimHandler,
                            adjusterPreferredNumberType);
                    }

                    serviceData.customerContactPreferredNumber = _.get(response.customerContact,
                        customerContactPreferredNumberType);
                    serviceData.claimHandlerPreferredNumber = _.get(response.claimHandler,
                        claimHandlerPreferredNumberType);

                    serviceData.invoiceTotal = {
                        amount: response.outstandingInvoiceAmount.amount
                            + response.paymentsMadeAmount.amount,
                        currency: response.outstandingInvoiceAmount.currency
                    };
                    serviceData.sortedHistory = _.sortBy(response.history, 'sequence');
                    if (response.additionalInstructions
                            && response.additionalInstructions.length > 100) {
                        serviceData.showFullInstructions = false;
                        serviceData.truncatedInstructions = _.truncate(
                            response.additionalInstructions, { length: 100 }
                        );
                    }
                    if (response.canDelay) {
                        serviceData.otherOperations.push('vendordelayed'); // Not a real SR Operation - but needed to reflect ability to delay date in CC
                    }
                    serviceData.showServiceChange = response.showServiceChange;
                    serviceData.showServiceRecovery = response.showServiceRecovery;

                    setAdjusterService(response.adjusterService);
                    updateCanAddInvoiceAX(shouldDisplayCanAddInvoiceAX);
                    setServiceDetails(serviceData);
                    // next line is required to refresh info in screen after edit / add documents
                    refreshData();
                })
                .finally(() => {
                    setLoadingState(false);
                });
        }, [PRIMARY_PHONE_TYPE_MAP, authHeader, refreshData, serviceRequestNumber, serviceItems]
    );

    const handleAcceptWork = useCallback(
        (actionData) => {
            ServiceRequestService.acceptWork(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleCompleteWork = useCallback(
        () => {
            ServiceRequestService.completeWork(serviceRequestNumber, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceRequestNumber]
    );

    const handleDeclineWork = useCallback(
        (actionData) => {
            ServiceRequestService.declineWork(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleCancelWork = useCallback(
        (actionData) => {
            ServiceRequestService.cancelServiceRequest(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleCancelFinishedWork = useCallback(
        () => {
            ServiceRequestService.cancelServiceFinishedAX(serviceRequestNumber, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceRequestNumber]
    );

    const handlePauseWork = useCallback(
        (actionData) => {
            ServiceRequestService.reportBlocked(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleDelayWork = useCallback(
        (actionData) => {
            ServiceRequestService.reportDelay(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleAddQuote = useCallback(
        (quoteVM) => {
            ServiceRequestService.addOrReplaceQuote(
                serviceItems.publicID, quoteVM.value, authHeader
            ).then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceItems]
    );

    const handleAddInvoice = useCallback(
        (invoiceVM) => {
            ServiceRequestService.addInvoice(
                serviceItems.publicID, invoiceVM.value, authHeader
            ).then(() => {
                const documentsArray = _.get(invoiceVM, 'documents.value', []);
                const hasPPD = documentsArray.filter(
                    (doc) => doc.ax_ServiceRequestStatementDTO.ppd
                ).length > 0;
                if (hasPPD) {
                    ToastProvider.toast({
                        message: translator(messages.hasPPDAXMsg),
                        autoClose: false,
                        type: 'info'
                    });
                }
                _.set(invoiceVM, 'documents.value', []);
                _.set(invoiceVM, 'description', ' ');

                getServiceRequestDetails();
            }).catch((error) => handleGenericErrorModal(error));
        }, [authHeader, getServiceRequestDetails, handleGenericErrorModal, serviceItems, translator]
    );
    const handleAddInvoiceSupplementAX = useCallback(
        (invoiceVM) => {
            ServiceRequestService.axAddInvoiceSupplement(
                serviceItems.publicID, invoiceVM.value, authHeader
            ).then(() => {
                getServiceRequestDetails();
            }).catch((error) => handleGenericErrorModal(error));
        }, [authHeader, getServiceRequestDetails, handleGenericErrorModal, serviceItems.publicID]
    );

    const handleWithdrawInvoice = useCallback(
        (withdrawInvoiceVM) => {
            ServiceRequestService.withdrawInvoice(
                withdrawInvoiceVM.value, authHeader
            ).then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleResumeWork = useCallback(
        () => {
            ServiceRequestService.resumeWork(serviceRequestNumber, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceRequestNumber]
    );

    const addNewInvoice = useCallback(() => {
        if (serviceItems.pendingSupplement) {
            showAlert({
                title: messages.pendingSupplementAlertTitleMsg,
                message: translator(messages.pendingSupplementAlertBodyMsg),
                status: 'warning',
                icon: 'mi-error-outline'
            });
        } else {
            showModal(
                <AddInvoicePopup
                    serviceRequestID={serviceItems.publicID}
                    existingDocuments={serviceItems.documents}
                    viewModelService={viewModelService}
                    authHeader={authHeader}
                />
            ).then(handleAddInvoice).catch(_.noop);
        }
    }, [handleAddInvoice, serviceItems, translator]);

    const performOperation = useCallback(
        (otherOperation) => {
            let actionProps = {
                serviceRequest: serviceItems,
                viewModelService: viewModelService
            };
            switch (otherOperation) {
                case 'specialistacceptedwork':
                    showModal(
                        <AcceptWorkActionPopup {...actionProps} 
                        viewModelService={viewModelService}
                        />
                    )
                        .then(handleAcceptWork)
                        .catch(_.noop);
                    break;
                case 'specialistcompletedwork':
                    showConfirm({
                        title: translator(messages.actionTitleCompleteWork),
                        message: translator(messages.actionMessageCompleteWork),
                        confirmButtonText: translator(messages.actionButtonCompleteWork),
                        cancelButtonText: translator(messages.actionButtonCancel)
                    })
                        .then(
                            (res) => {
                                if (res === 'confirm') {
                                    handleCompleteWork();
                                }
                            }
                        )
                        .catch(_.noop);
                    break;
                case 'specialistdeclined':
                    actionProps = {
                        ...actionProps,
                        modalTitle: translator(messages.actionTitleDeclineWork),
                        modalMessage: translator(messages.actionMessageDeclineWork),
                        defaultButtonLabel: translator(messages.actionButtonDeclineWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(handleDeclineWork)
                        .catch(_.noop);
                    break;
                case 'specialistcanceled':
                    actionProps = {
                        ...actionProps,
                        modalTitle: translator(messages.actionTitleCancelWork),
                        modalMessage: translator(messages.actionMessageCancelWork),
                        defaultButtonLabel: translator(messages.actionButtonCancelWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(handleCancelWork)
                        .catch(_.noop);
                    break;
                case 'specialistwaiting':
                    actionProps = {
                        ...actionProps,
                        modalTitle: translator(messages.actionTitlePauseWork),
                        modalMessage: translator(messages.actionMessagePauseWork),
                        defaultButtonLabel: translator(messages.actionButtonPauseWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(handlePauseWork)
                        .catch(_.noop);
                    break;
                case 'addquote':
                    showModal(
                        <AddQuoteModal
                            quoteValue={serviceItems.latestQuote}
                            serviceRequestID={serviceItems.publicID}
                            existingDocuments={serviceItems.documents}
                            viewModelService={viewModelService}
                            authHeader={authHeader}

                        />
                    )
                        .then(handleAddQuote)
                        .catch(_.noop);
                    break;
                case 'addinvoice':
                    addNewInvoice();
                    break;
                case 'vendordelayed':
                    showModal(
                        <DelayWorkActionPopup {...actionProps} />
                    )
                        .then(handleDelayWork)
                        .catch(_.noop);
                    break;
                case 'specialistresumedwork':
                    showConfirm({
                        title: translator(messages.actionTitleResumeWork),
                        message: translator(messages.actionMessageResumeWork),
                        confirmButtonText: translator(messages.actionButtonResumeWork),
                        cancelButtonText: translator(messages.actionButtonCancel)
                    })
                        .then(
                            (res) => {
                                if (res === 'confirm') {
                                    handleResumeWork();
                                }
                            }
                        )
                        .catch(_.noop);
                    break;
                case 'cancelServiceFinished':
                    showConfirm({
                        icon: 'mi-info-outline',
                        status: 'warning',
                        title: translator(messages.actionTitleCancelWork),
                        message: translator(messages.actionMessageCancelFinishedWork),
                        confirmButtonText: translator(messages.actionButtonYes),
                        cancelButtonText: translator(messages.actionButtonNo)
                    })
                        .then(
                            (res) => {
                                if (res === 'confirm') {
                                    handleCancelFinishedWork();
                                }
                            }
                        )
                        .catch(_.noop);
                    break;
                default:
                    break;
            }
        }, [
            handleAcceptWork,
            handleAddQuote,
            handleAddInvoice,
            handleCompleteWork,
            handleDeclineWork,
            handlePauseWork,
            handleDelayWork,
            handleCancelWork,
            handleCancelFinishedWork,
            handleResumeWork,
            serviceItems,
            translator,
            viewModelService
        ]
    );

    const generateActionItemOverrides = useMemo(
        () => {
            const { otherOperations } = serviceDetails;
            const actionOverrides = otherOperations.map((otherOperation, index) => {
                return {
                    [`actionItem${index}`]: {
                        content: lookupActionNameMessageKey(otherOperation),
                        onClick: () => performOperation(otherOperation)
                    }
                };
            });
            return Object.assign({}, ...actionOverrides);
        }, [lookupActionNameMessageKey, performOperation, serviceDetails]
    );

    const mailToTrigger = useCallback((event, email) => {
        event.preventDefault();
        window.location = `mailto:${email}`;
    }, []);

    const renderEmailLink = useCallback((emailID) => {
        return (
            <Link className={styles.link} onClick={(e) => mailToTrigger(e, emailID)} href="/">
                {emailID}
            </Link>
        );
    }, [mailToTrigger]);

    const phoneTrigger = useCallback((event, phoneNumber) => {
        event.preventDefault();
        window.location = `tel:${phoneNumber}`;
    }, []);

    const getDateModified = useCallback((items, index, { path: property }) => {
        if (typeof items[property] === 'undefined') {
            return '';
        }
        return <FormattedDate value={new Date(items[property])} format="short" />;
    }, []);

    const renderPhoneLink = useCallback((phone) => {
        return (
            <Link className={styles.link} onClick={(e) => phoneTrigger(e, phone)} href="/">
                {phone}
            </Link>
        );
    }, [phoneTrigger]);

    const getCellPremium = useCallback((rowData, rowIndex) => {
        return (
            <CurrencyField
                id={`totalamount_${rowIndex}`}
                value={rowData.totalAmount}
                readOnly
            />
        );
    }, []);

    const addNewMessage = useCallback(() => {
        showModal(
            <CreateNewMessagePopup
                serviceRequestNumber={serviceRequestNumber}
                authHeader={authHeader}
                serviceDetails={serviceDetails}
                viewModelService={viewModelService}
            />
        ).then(getServiceRequestDetails);
    }, [authHeader, getServiceRequestDetails, serviceRequestNumber, serviceDetails,viewModelService]);

    const addNewDocument = useCallback(() => {
        showModal(
            <AddDocumentPopupAX
                serviceRequestID={_.get(serviceItems, 'publicID')}
                existingDocuments={_.get(serviceItems, 'documents')}
                viewModelService={viewModelService}
                authHeader={authHeader}
            />
        ).then(getServiceRequestDetails);
    }, [getServiceRequestDetails, serviceItems,viewModelService]);

    const replyMessage = useCallback((messageData) => {
        showModal(
            <ReplyMessagePopup
                serviceRequestNumber={serviceRequestNumber}
                serviceItems={serviceItems}
                messageData={messageData}
                viewModelService={viewModelService}
                authHeader={authHeader}
            />
        ).then(getServiceRequestDetails);
    }, [getServiceRequestDetails, serviceItems, serviceRequestNumber, viewModelService]);

    const getInvoiceStatus = useCallback((rowData) => {
        return translator({
            id: `typekey.ServiceRequestInvoiceStatus.${rowData.status}`,
            defaultMessage: rowData.status
        });
    }, [translator]);

    const getMessageType = useCallback((rowData) => {
        return translator({
            id: `typekey.ServiceRequestMessageType.${rowData.type}`,
            defaultMessage: rowData.type
        });
    }, [translator]);

    const withdrawInvoice = useCallback((invoiceData) => {
        showModal(
            <WithdrawInvoicePopup invoiceID={invoiceData.publicID} 
            viewModelService={viewModelService}
            />
        ).then(handleWithdrawInvoice);
    }, [handleWithdrawInvoice,viewModelService]);

    const getDocumentDownloadLink = useCallback(
        (item) => {
            // const { publicID, sessionID } = item;
            // const linkHref = ServiceRequestService.downloadDocumentLink(publicID, sessionID);
            const msjObject = {
                id: `typekey.DocumentType.${item.documentType}`,
                defaultMessage: 'N/A'
            };
            const linkHref = item.ax_ECM_URL;

            if ((item.name).trim() === '') {
                item.name = translator(msjObject);
                
                const docDto1 = viewModelService.create(item, 'cc', 'edge.capabilities.document.dto.DocumentBaseDTO');
                const updateDocsDTO1 = viewModelService.create({
                serviceRequestNumber: serviceRequestNumber,
                documents: []
                }, 'cc', 'edge.capabilities.servicerequest.dto.AX_DocumentGroup');

                updateDocsDTO1.documents.value.push(docDto1.value);
                ServiceRequestService.updateDocumentMetadataAX(updateDocsDTO1.value, authHeader)
                .then(() => {
                _.get(serviceItems, 'documents').filter((tmp) => tmp.publicID === item.publicID)[0].name = item.name;
                refreshData();
                })
                
                return translator(msjObject);

            } else {

            return (
                <Link
                    target="_blank"
                    href={linkHref}
                    className={styles.documentNameLink}
                >
                    {item.name}
                </Link>
                );
        }},
        [translator, authHeader, handleGenericErrorModal, refreshData, serviceItems, serviceRequestNumber, viewModelService]
    );

    const getDocumentTypeDropdown = useCallback((item) => {
        const msjObject = {
            id: `typekey.DocumentType.${item.documentType}`,
            defaultMessage: 'N/A'
        };
        return translator(msjObject);
    }, [translator]);

const onDeleteDoc = useCallback((doc) => {
        //DocumentTypes that are not available to delete
        const docsType = ['AX_medical_report', 'AX_settlement','AX_id_identification','AX_supplier_invoices',
        'AX_InvoiceSupplement'];

        if (!docsType.includes(doc.documentType)){
        showConfirm({
            title: messages.genericConfirmTitleMsg,
            message: translator(messages.confirmDeleteDocumentMsg, {
                docName: doc.name
            }),
            status: 'warning',
            icon: 'mi-help'
        }).then((response) => {
            if (response === 'confirm') {
                setLoadingState(true);
                ServiceRequestService.deleteDocumentAX(_.get(serviceItems, 'publicID'), doc.publicID, authHeader)
                    .then((sucess) => {
                        if (sucess) {
                            _.set(serviceItems, 'documents',
                                _.get(serviceItems, 'documents').filter((item) => item.publicID !== doc.publicID));
                            refreshData();
                        } else {
                            handleGenericErrorModal({ errorCode: 'Remote', codeKey: 'CN' });
                        }
                    })
                    .catch((error) => {
                        handleGenericErrorModal(error);
                    })
                    .finally(() => {
                        setLoadingState(false);
                    });
            }
        });
    } else {
        showAlert({
            title: messages.WarningMessageAX,
            message: translator(messages.canDeleteDocAX),
            status: 'error',
            icon: 'mi-error-outline'
        })
    }}, [authHeader, handleGenericErrorModal, refreshData, serviceItems, translator]);

    const onEditDoc = useCallback((doc) => {
        if ((doc.name).trim() === '') {
            showAlert({
                title: messages.WarningMessageAX,
                message: translator(messages.cantEditDocAX),
                status: 'error',
                icon: 'mi-error-outline'
            })
        } else {
        setLoadingState(true);
        const docDto = viewModelService.create(doc, 'cc', 'edge.capabilities.document.dto.DocumentBaseDTO');
        const updateDocsDTO = viewModelService.create({
            serviceRequestNumber: serviceRequestNumber,
            documents: []
        }, 'cc', 'edge.capabilities.servicerequest.dto.AX_DocumentGroup');
        updateDocsDTO.documents.value.push(docDto.value);
        ServiceRequestService.updateDocumentMetadataAX(updateDocsDTO.value, authHeader)
            .then(() => {
                _.get(serviceItems, 'documents').filter((tmp) => tmp.publicID === doc.publicID)[0].name = doc.name;
                refreshData();
            })
            .catch((error) => {
                handleGenericErrorModal(error);
            })
            .finally(() => {
                setLoadingState(false);
            });
    }}, [authHeader, handleGenericErrorModal, refreshData,
        serviceItems, serviceRequestNumber, viewModelService
    ]);

    const addNewInvoiceSupplementAX = useCallback(() => {
        showModal(
            <AddInvoicePopup
                serviceRequestID={serviceItems.publicID}
                existingDocuments={serviceItems.documents}
                authHeader={authHeader}
                invoiceSupplementAX="true"
                viewModelService={viewModelService}
            />
        ).then(handleAddInvoiceSupplementAX);
    }, [handleAddInvoiceSupplementAX, serviceItems,viewModelService]);

    const getCellInvoiceSupplementAX = useCallback((item) => {
        return ((item.ax_Can_Add_Supplement && item.status === 'ax_paid'
                && !serviceItems.relatedBulkInvoice)
        && (
            <Button
                icon="mi-add"
                size="small"
                type="filled"
                onClick={addNewInvoiceSupplementAX}
            >
                {translator(messages.addSupplementAX)}
            </Button>
        )
        );
    }, [addNewInvoiceSupplementAX, translator, serviceItems]);

    const updateMedicalPackage = useCallback(() => {
        showModal(
            <UpdateMedicalPackagePopup
                serviceRequestNumber={serviceRequestNumber}
                authHeader={authHeader}
                viewModelService={viewModelService}
            />
        ).then(getServiceRequestDetails);
    }, [serviceRequestNumber, authHeader, getServiceRequestDetails,viewModelService]);

    const updateTheftResolution = useCallback(() => {
        showModal(
            <UpdateTheftResolutionPopup
                serviceRequestNumber={serviceRequestNumber}
                authHeader={authHeader}
                viewModelService={viewModelService}
            />
        ).then(getServiceRequestDetails);
    }, [serviceRequestNumber, authHeader, getServiceRequestDetails,viewModelService]);

    const updateAmountService = useCallback(() => {
        showModal(
            <UpdateAmountServicePopupAX
                serviceRequestNumber={serviceRequestNumber}
                authHeader={authHeader}
                viewModelService={viewModelService}
            />
        ).then(getServiceRequestDetails);
    }, [serviceRequestNumber, authHeader, getServiceRequestDetails,viewModelService]);

    const getReferenceNumberAX = useCallback((referenceNumber, isGlassService) => {
        if (isGlassService && referenceNumber !== undefined) {
            const nags = referenceNumber.split(',');
            if (nags.length > 4) {
                return `${nags.slice(0, 4).toString()}...`;
            }
        }
        return referenceNumber;
    }, []);

    const getWithdrawButton = useCallback((invoiceData) => {
        if (!invoiceData.canWithdraw) {
            return null;
        }

        return (
            <Button
                id="withdrawButton"
                type="secondary"
                icon="mi-delete"
                onClick={() => withdrawInvoice(invoiceData)}
            />
        );
    }, [withdrawInvoice]);

    useEffect(() => {
        getServiceRequestDetails();
        // disable re render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const override = {
        '@field': {
            labelPosition: 'left'
        },
        serviceRequestsDetailsTitle: {
            content: `${_.get(serviceItems, 'customerContact.displayName')} - ${_.get(serviceItems, 'claimID')}`
        },
        serviceAddressLatLng: {
            content: `${_.get(serviceItems, 'claimAddressLocation')}`
        },
        customerContactEmail: {
            content: renderEmailLink(_.get(serviceItems, 'customerContact.emailAddress1'))
        },
        claimHandlerEmail: {
            content: renderEmailLink(_.get(serviceItems, 'claimHandler.emailAddress1'))
        },
        claimHandlerContact: {
            content: renderPhoneLink(_.get(serviceDetails, 'claimHandlerPreferredNumber'))
        },
        customerContactNumber: {
            content: renderPhoneLink(_.get(serviceDetails, 'customerContactPreferredNumber'))
        },
        actionsDropdown: {
            visible: serviceItems.showAction && serviceDetails.otherOperations.length > 0
        },
        addInvoiceButtonContainer: {
            visible: canAddInvoiceAX
        },
        actionDefaultButton: {
            content: lookupActionNameMessageKey(serviceDetails.firstOperation),
            onClick: () => performOperation(serviceDetails.firstOperation),
            visible: serviceItems.showAction && !_.isUndefined(serviceDetails.firstOperation)
        },
        ...generateActionItemOverrides,
        messageTable: {
            data: _.get(serviceItems, 'messages'),
            onRowClick: replyMessage
        },
        invoiceTable: {
            data: _.get(serviceItems, 'invoices')
        },
        historyTable: {
            data: _.get(serviceItems, 'history')
        },
        documentTable: {
            data: _.get(serviceItems, 'documents'),
            onSaveRow: onEditDoc
        },
        adjusterDocumentTable: {
            data: _.get(adjusterService, 'documents', [])
        },
        invoiceTotalAmountColumn: {
            renderCell: getCellPremium
        },
        invoiceStatusColumn: {
            renderCell: getInvoiceStatus
        },
        documentName: {
            renderCell: getDocumentDownloadLink
        },
        documentType: {
            renderCell: getDocumentTypeDropdown
        },
        typeColumn: {
            renderCell: getMessageType
        },
        adjusterDocumentName: {
            renderCell: getDocumentDownloadLink
        },
        adjusterDocumentType: {
            renderCell: getDocumentTypeDropdown
        },
        deleteAction: {
            onClick: onDeleteDoc
        },
        referenceNumberLinkAX: {
            content: getReferenceNumberAX(serviceItems.referenceNumber,
                serviceItems.ax_IsGlassService),
            icon: 'mi-edit',
            to: `/nagPage/${serviceRequestNumber}`
        },
        nagTitleDivAX: {
            visible: serviceItems.ax_IsGlassService,
        },
        axMedicalPackageTitleContainer: {
            visible: serviceItems.ax_InstructionType === 'AX_MedicalTreatmentAditionalInstruction'
        },
        axMedicalPackageLink: {
            content: translateMedicalPackageForCode(serviceItems.ax_MedicalPackage)
        },
        axUpdateTheftResolutionTitleContainer: {
            visible: serviceItems.ax_InstructionType === 'AX_ThefAdditionalInstruction'
        },
        axUpdateTheftResolutionLink: {
            content: translateTheftResolutionForCode(serviceItems.ax_TheftResolution)
        },
        adjusterDataContainer: {
            visible: _.get(serviceItems, 'showAdjusterSection'),
        },
        adjusterEmail: {
            content: renderEmailLink(_.get(serviceItems, 'adjusterService.claimHandler.emailAddress1'))
        },
        adjusterPrimaryPhone: {
            content: renderPhoneLink(_.get(serviceDetails, 'adjusterPreferredNumber'))
        },
        vehicleDetails: {
            vehicleDetails: serviceItems.vehicleInvolved
        },
        amountServiceContainerAX: {
            visible: serviceItems.ax_ShowAmountService
        },
        claimDetailsRequestNumber_AX: {
            visible: serviceItems.ax_IsInjuredIncidentRelated
        },
        injuredIncidentDiv: {
            visible: serviceItems.ax_IsInjuredIncidentRelated
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveValue: readValue,
        resolveComponentMap: {
            vehicleDetailsComponent: VehicleDetailsComponentAX
        },
        resolveCallbackMap: {
            getCellInvoiceSupplementAX: getCellInvoiceSupplementAX,
            getDateModified: getDateModified,
            addNewInvoice: addNewInvoice,
            addNewMessage: addNewMessage,
            getWithdrawButton: getWithdrawButton,
            updateMedicalPackage: updateMedicalPackage,
            updateTheftResolution: updateTheftResolution,
            addNewDocument: addNewDocument,
            updateAmountService: updateAmountService
        }
    };

    return renderContentFromMetadataInternal(metadata.pageContent, override, resolvers);
}

ServiceRequestDetailsPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            serviceRequestNumber: PropTypes.string
        })
    }).isRequired
};

export default withRouter(withViewModelService(ServiceRequestDetailsPage));
